/**
 * help-dialog.js
 *
 * Use this component to show a form help dialog for mobile devices. Desktop users see form help text in the side
 * panel instead. The dialog can display help info for either EditorContent (panels/tabs within the Editor) or an
 * EditorSection (group of form fields within a panel). Appropriate help text is extracted and translated from a
 * JSON file defined by the translation prop. The presence of a sectionName prop tells the component to extract
 * help information for a specific form field section. Otherwise, help is shown for the current panel.
 */

import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Zoom,
  Typography,
} from '@mui/material';
import Icon from 'components/icon';


const Transition = React.forwardRef(function Transition(props, ref) {
  return <Zoom ref={ref} {...props} />;
});


const HelpTitle = (props) => {
  return (
    <Typography
      variant='subtitle1'
      sx={{
        fontWeight: 600,
        color: 'sys.onSurface',
        pb: 1.5,
      }}
      {...props}
    />
  );
}


const HelpItem = (props) => {
  const { gutterBottom, ...rest } = props;

  return (
    <Typography
      variant='body2'
      component='div'
      sx={{
        whiteSpace: 'pre-line',
        color: 'grey.colors.2',
        pb: gutterBottom ? 1.5 : 0,
      }}
      {...rest}
    />
  );
}


export default function HelpDialog(props) {
  const {
    open,
    onClose,
    iconName,
    title,
    helpFieldList,
  } = props;


  return (
    <>
      {helpFieldList?.length > 0 && 
      <Dialog
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
        transitionDuration={300}
      >
        <DialogTitle 
          sx={{ 
            justifyContent: 'center', 
            py: { xs: 2 }, // using the media syntax allowed overriding when it wouldn't otherwise
          }} 
          className={`flex-hc`}
        >
          <Icon type={iconName} sx={{ pr: 1 }} />
          <span style={{textTransform: "uppercase"}}>
            {title}
          </span>
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {helpFieldList.map(([key, val]) => {
              const isHelpInfoList = Array.isArray(val.helpInfo);
              return (
                <Box key={key}>
                  {val.helpTitle &&
                    <HelpTitle>{val.helpTitle}</HelpTitle>
                  }

                  {/* regular help info */}
                  {!isHelpInfoList &&
                  <HelpItem gutterBottom>{val.helpInfo}</HelpItem>}
                  
                  {/* list help info */}
                  {isHelpInfoList &&
                  <Box sx={{ pb: 1.5 }}>
                    <ol style={{ margin: 0, paddingInlineStart: 24, }}>
                      {val.helpInfo.map((listItem, i) => (
                        <li key={i}><HelpItem noGutter>{listItem}</HelpItem></li>
                      ))}
                    </ol>
                  </Box>}
                </Box>
              );
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" autoFocus>
            <strong>OK</strong>
          </Button>
        </DialogActions>
      </Dialog>}
    </>
  );
}
